import { useRef } from "react";
import { motion, useReducedMotion, useInView } from "framer-motion";

const ease = [0.16, 1, 0.3, 1];

// Fade-and-rise reveal on scroll into view.
export const Reveal = ({ children, delay = 0, y = 28, className = "", as = "div", ...rest }) => {
  const reduce = useReducedMotion();
  const M = motion[as] || motion.div;
  return (
    <M
      className={className}
      initial={reduce ? { opacity: 0 } : { opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-10% 0px -10% 0px" }}
      transition={{ duration: 1, ease, delay }}
      {...rest}
    >
      {children}
    </M>
  );
};

// Line-by-line masked reveal for display headings.
export const MaskLines = ({ lines, className = "", lineClassName = "", delay = 0, as = "span" }) => {
  const reduce = useReducedMotion();
  const Tag = as;
  return (
    <Tag className={className} aria-label={lines.join(" ")}>
      {lines.map((line, i) => (
        <span key={i} className="block overflow-hidden">
          <motion.span
            aria-hidden="true"
            className={`block ${lineClassName}`}
            initial={reduce ? { opacity: 0 } : { y: "110%" }}
            animate={reduce ? { opacity: 1 } : { y: 0 }}
            transition={{ duration: 1.1, ease, delay: delay + i * 0.12 }}
          >
            {line}
          </motion.span>
        </span>
      ))}
    </Tag>
  );
};

// Slow image reveal via clip-path. Uses useInView so above-the-fold images
// reveal reliably on mount and are never left permanently clipped/hidden.
export const ImageReveal = ({ src, alt, className = "", imgClassName = "" }) => {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.15 });
  const revealed = reduce || inView;
  return (
    <div ref={ref} className={`overflow-hidden ${className}`}>
      <motion.img
        src={src}
        alt={alt}
        loading="lazy"
        className={`h-full w-full object-cover ${imgClassName}`}
        initial={false}
        animate={
          reduce
            ? { opacity: 1 }
            : { clipPath: revealed ? "inset(0% 0 0 0)" : "inset(100% 0 0 0)", scale: revealed ? 1 : 1.08 }
        }
        transition={{ duration: 1.3, ease }}
      />
    </div>
  );
};
